// Stillhouse view — Performance defaults. Ported verbatim from the design.
import React, { useEffect, useState } from "react";
import { C, F, Btn } from "../brand/apo";
import { Card, SectionLabel, Chip, Toggle, Bar, PageHeader, EmptyState, COPPER } from "../brand/sh";
import { api, type Team } from "../api";

const Scroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ flex: 1, overflow: "auto", padding: "22px 32px", display: "flex", flexDirection: "column", gap: 16 }}>{children}</div>
);
function Row({ title, sub, children, last }: { title: React.ReactNode; sub?: React.ReactNode; children?: React.ReactNode; last?: boolean }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "15px 18px", borderBottom: last ? "none" : `1px solid ${C.line}` }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 14, color: C.fgDark, fontWeight: 500 }}>{title}</div>
        {sub && <div style={{ fontSize: 12.5, color: C.fgMute, marginTop: 3, lineHeight: 1.45 }}>{sub}</div>}
      </div>
      {children}
    </div>
  );
}

export function Performance() {
  const [teams, setTeams] = useState<Team[]>([]);
  const [team, setTeam] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api
      .getTeams()
      .then((r) => {
        if (!alive) return;
        setTeams(r.teams);
        setTeam(r.teams[0]?.name ?? null);
      })
      .catch(() => alive && setTeams([]));
    return () => {
      alive = false;
    };
  }, []);

  const support: [string, string][] = [
    ["Plan", "Dedicated · 24/7"],
    ["Response SLA", "< 1 hour"],
    ["Escalation", "Priority queue"],
  ];
  return (
    <React.Fragment>
      <PageHeader
        crumb="ACCOUNT › PERFORMANCE"
        title="Performance defaults"
        sub="Team-level streaming profiles — bandwidth, resolution, codec and concurrency. Operators inherit these unless an admin grants an override."
      >
        <Btn primary>Save profile</Btn>
      </PageHeader>
      <Scroll>
        {teams.length === 0 ? (
          <Card pad={false}>
            <EmptyState title="No teams yet" sub="CREATE A TEAM TO SET PER-TEAM PROFILES · ORG DEFAULTS BELOW" />
          </Card>
        ) : (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {teams.map((t) => (
              <button
                key={t.id}
                onClick={() => setTeam(t.name)}
                style={{
                  padding: "8px 16px",
                  fontFamily: F.ui,
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                  background: team === t.name ? C.glow : C.surface,
                  color: team === t.name ? C.ink : C.fgDark,
                  border: `1px solid ${team === t.name ? C.glow : C.line}`,
                }}
              >
                {t.name}
              </button>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          <Card title={`${team ?? "Default"} · bandwidth & quality`} pad={false}>
            <div style={{ padding: "16px 18px", borderBottom: `1px solid ${C.line}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
                <SectionLabel>Bandwidth cap</SectionLabel>
                <span style={{ fontFamily: F.mono, color: C.glow, fontWeight: 700 }}>35 Mbps</span>
              </div>
              <Bar value={35} max={60} color={C.glow} height={8} />
              <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, fontFamily: F.mono, fontSize: 10, color: C.fgFaint }}>
                <span>5</span>
                <span>60 Mbps</span>
              </div>
            </div>
            <Row title="Max resolution" sub="Per-session ceiling">
              <Chip color={C.lilac} solid>
                3840×2160
              </Chip>
            </Row>
            <Row title="Frame rate" sub="Target refresh">
              <Chip color={C.fgMute}>120 fps</Chip>
            </Row>
            <Row title="Codec" sub="Negotiated with host GPU">
              <div style={{ display: "flex", gap: 6 }}>
                <Chip color={C.glow} solid>
                  AV1
                </Chip>
                <Chip color={C.fgMute}>HEVC</Chip>
                <Chip color={C.fgMute}>H.264</Chip>
              </div>
            </Row>
            <Row title="HDR passthrough" last>
              <Toggle defaultOn />
            </Row>
          </Card>
          <Card title={`${team ?? "Default"} · sessions & limits`} pad={false}>
            <Row title="Concurrent sessions / member" sub="Independent streams one operator can hold">
              <Chip color={C.fgMute}>2</Chip>
            </Row>
            <Row title="Concurrent sessions / machine" sub="Multi-seat hosts">
              <Chip color={C.fgMute}>1</Chip>
            </Row>
            <Row title="Adaptive bitrate" sub="Drop quality before latency on congestion">
              <Toggle defaultOn />
            </Row>
            <Row title="Hardware encode required" sub="Refuse software-encoded hosts">
              <Toggle defaultOn />
            </Row>
            <Row title="Allow per-user override" sub="Team leads can raise caps for a member" last>
              <Toggle />
            </Row>
          </Card>
        </div>

        <Card title="Priority support" tag={<Chip color={COPPER} solid>Enterprise</Chip>}>
          <div style={{ display: "flex", gap: 40, flexWrap: "wrap" }}>
            {support.map((s, i) => (
              <div key={i}>
                <SectionLabel>{s[0]}</SectionLabel>
                <div style={{ fontSize: 15, color: C.fgDark, marginTop: 6, fontFamily: F.display, fontWeight: 500 }}>{s[1]}</div>
              </div>
            ))}
          </div>
        </Card>
      </Scroll>
    </React.Fragment>
  );
}
